import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const LOCAL_KEY = "bayzo_recent_searches";
const MAX = 8;

export const useRecentSearches = () => {
  const [recent, setRecent] = useState<string[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  const loadLocal = (): string[] => {
    try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]"); } catch { return []; }
  };

  const loadRemote = useCallback(async (uid: string) => {
    const { data } = await supabase
      .from("recent_searches")
      .select("query, searched_at")
      .eq("user_id", uid)
      .order("searched_at", { ascending: false })
      .limit(MAX);
    const queries = Array.from(new Set((data ?? []).map((r) => r.query)));
    setRecent(queries.slice(0, MAX));
  }, []);

  useEffect(() => {
    setRecent(loadLocal());
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
        await loadRemote(session.user.id);
      }
    };
    init();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setUserId(s?.user?.id ?? null);
      if (s?.user) loadRemote(s.user.id);
    });
    return () => subscription.unsubscribe();
  }, [loadRemote]);

  const addSearch = async (q: string) => {
    const query = q.trim();
    if (!query) return;
    setRecent((prev) => {
      const next = [query, ...prev.filter((x) => x.toLowerCase() !== query.toLowerCase())].slice(0, MAX);
      localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
      return next;
    });
    if (userId) {
      await supabase.from("recent_searches").insert({ user_id: userId, query });
    }
  };

  const clearAll = async () => {
    setRecent([]);
    localStorage.removeItem(LOCAL_KEY);
    if (userId) await supabase.from("recent_searches").delete().eq("user_id", userId);
  };

  return { recent, addSearch, clearAll };
};
